import React from 'react'

import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import { formatCommas } from '@utils/formatters/number.formatter'
import { ThemeType } from '@utils/global'
import Storage from '@utils/storage'

import type { ByScoreResponse } from '@pages/home/loaders/dashboard.loader'

ChartJS.register(ArcElement, Tooltip, Legend)

interface Props {
  resp: ByScoreResponse
}

const PIE_BACKGROUND_COLORS = ['#81BFF5', '#EDC24D', '#F74E20', '#DDE1EB', '#17A799']
const NO_DATA_BACKGROUND_COLOR = '#e0d9d2'

const ByScorePieChart: React.FC<Props> = ({ resp: { data_points } }) => {
  const { t } = useTranslation()

  // Tooltip
  let tooltipEnabled = true

  const chartData: number[] = []
  const chartLabels: string[] = []
  const legendElements: { title: string; count: number }[] = []
  let total = 0

  const points = [...data_points.top]
  if (data_points.remainder) {
    points.push({ title: t('content.otherScores'), count: data_points.remainder })
  }

  for (const p of points.sort((a, b) => b.count - a.count)) {
    chartData.push(p.count)
    total += p.count
    chartLabels.push(p.title)
    legendElements.push(p)
  }

  let backgroundColors = PIE_BACKGROUND_COLORS.slice(0, chartLabels.length)

  // Empty graph?
  if (points.length === 0) {
    chartData.push(1)
    chartLabels.push(t('content.noScoresShown'))
    backgroundColors = [NO_DATA_BACKGROUND_COLOR]
    tooltipEnabled = false
  }

  return (
    <>
      <span className="absolute top-1 right-2.5 text-[50px] leading-tight">{total}</span>
      <div className="flex flex-row items-center flex-1 min-w-0">
        <div className="w-full max-w-[37.5%] p-4">
          <Doughnut
            data={{
              labels: chartLabels,
              datasets: [
                {
                  data: chartData,
                  backgroundColor: backgroundColors,
                },
              ],
            }}
            options={{
              responsive: true,
              aspectRatio: 1,
              borderColor: Storage.theme === ThemeType.DARK ? '#31363A' : '#FFFFFF',
              cutout: '80%',
              plugins: {
                legend: {
                  display: false,
                },
                tooltip: {
                  enabled: tooltipEnabled,
                  bodyFont: {
                    size: 9,
                    family: 'Graphik, sans-serif, Ariel, Helvetica, Georgia',
                  },
                  displayColors: false,
                },
              },
            }}
            updateMode="resize"
          />
        </div>
        <div className="flex flex-col w-[62.5%] px-2 justify-center h-full">
          {legendElements.map((element, index) => (
            <div
              className="flex flex-row items-center mb-1.5 text-md"
              key={`legend-element-${element.title}`}>
              <div
                className="rounded-full mr-1.5 w-2 h-2"
                style={{ backgroundColor: PIE_BACKGROUND_COLORS[index] }}
              />
              <p className="ellipsify mr-1.5">{element.title}</p>
              <p className="font-medium " style={{ color: PIE_BACKGROUND_COLORS[index] }}>
                {formatCommas(element.count)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default ByScorePieChart
